import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Coins, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import AppHeader from '../components/AppHeader';

function WalletHistory() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async (cursor = null) => {
    try {
      if (cursor) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/wallet/history?limit=30`;
      if (cursor) {
        url += `&cursor=${encodeURIComponent(cursor)}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (cursor) {
          setTransactions(prev => [...prev, ...(data.transactions || [])]);
        } else {
          setTransactions(data.transactions || []);
        }
        setHasMore(data.has_more || false);
        setNextCursor(data.next_cursor || null);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (hasMore && nextCursor && !loadingMore) {
      fetchHistory(nextCursor);
    }
  };

  // Format reason code to human-readable text using i18n
  const formatReasonCode = (code) => {
    const translated = t(`tx.${code}`, '');
    if (translated && translated !== `tx.${code}`) {
      return translated;
    }
    return code;
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diff = now - date;
      
      // Less than 24 hours - show relative time
      if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        if (hours < 1) {
          const minutes = Math.floor(diff / 60000);
          return isRTL ? `منذ ${minutes} دقيقة` : `${minutes}m ago`;
        }
        return isRTL ? `منذ ${hours} ساعة` : `${hours}h ago`;
      }
      
      // Use Intl.DateTimeFormat for proper localization
      const dateFormatter = new Intl.DateTimeFormat(t('accoreco.enus'), {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
      
      const timeFormatter = new Intl.DateTimeFormat(t('accoreco.enus'), {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      return `${dateFormatter.format(date)} • ${timeFormatter.format(date)}`;
    } catch {
      return dateStr;
    }
  };
  
  // Format amount with proper sign (no double negative)
  const formatAmount = (amount, isEarn) => {
    const absAmount = Math.abs(amount);
    if (absAmount === 0) return '0';
    return isEarn ? `+${absAmount}` : `-${absAmount}`;
  };

  return (
    <div className="min-h-screen bg-[#f6f7f8]" dir={isRTL ? 'rtl' : 'ltr'}>
      <AppHeader
        title={t('wallhist.transactionHistory')}
        onBack={() => navigate('/wallet', { state: { from: location.pathname } })}
        testId="wallet-history-header"
      />

      {/* Content */}
      <div className="max-w-[560px] mx-auto px-4 py-4 pb-24">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 size={32} className="animate-spin text-green-600 mb-3" />
            <p className="text-slate-500">{t('crophome.loading')}</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <Coins size={32} className="text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-1">
              {t('wallhist.noTransactionsYet')}
            </h3>
            <p className="text-sm text-slate-500 text-center max-w-[240px]">
              {t('wallhist.startEarningCoinsThroughDaily')}
            </p>
          </div>
        ) : (
          <>
            {/* Transactions List */}
            <div className="space-y-2">
              {transactions.map((txn, idx) => {
                // Determine if this is an earn (positive) or spend (negative) transaction
                const isEarn = txn.direction === '+' || (txn.type === 'earn' && txn.direction !== '-');
                return (
                  <div 
                    key={txn.transaction_id || txn.tx_id || `txn-${idx}`}
                    className="bg-white rounded-2xl border border-black/5 p-4 flex items-center gap-3"
                  >
                    {/* Icon */}
                    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                      isEarn ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                      {isEarn ? (
                        <TrendingUp size={18} className="text-green-600" />
                      ) : (
                        <TrendingDown size={18} className="text-red-500" />
                      )}
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {formatReasonCode(txn.reason_code || txn.reason)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDate(txn.created_at)}
                      </p>
                    </div>
                    
                    {/* Amount */}
                    <div className="flex flex-col items-end">
                      <span className={`font-bold text-lg ${
                        isEarn ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {formatAmount(txn.amount, isEarn)}
                      </span>
                      <span className="text-xs text-slate-400">
                        {t('coins')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-6 py-2.5 rounded-xl bg-white border border-black/10 text-slate-700 font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  {loadingMore ? (
                    <span className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      {t('crophome.loading')}
                    </span>
                  ) : (
                    t('wallhist.loadMore')
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default WalletHistory;
